import { CostTrendChart } from '@src/components/cost/CostTrendChart';
import { PageHeader } from '@src/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { errorMessage } from '@src/lib/errors';
import { resources } from '@src/resources';
import {
  aggregateDailyCostTotals,
  formatCostUsd,
  getDailyStats,
  getStats,
  rankByCost,
  scopeLabel,
} from '@src/services/opsService';
import { useQuery } from '@tanstack/react-query';

const TREND_DAYS = 14;
const STATS_WINDOW = '24h';

export function Cost() {
  const dailyQuery = useQuery({
    queryKey: ['ops', 'daily', TREND_DAYS],
    queryFn: () => getDailyStats(TREND_DAYS),
  });

  const statsQuery = useQuery({
    queryKey: ['ops', 'stats', STATS_WINDOW],
    queryFn: () => getStats(STATS_WINDOW),
  });

  const isLoading = dailyQuery.isLoading || statsQuery.isLoading;
  const isError = dailyQuery.isError || statsQuery.isError;
  const errorDetail = [dailyQuery.error, statsQuery.error].filter(Boolean).map(errorMessage).join(' · ');

  const dailyTotals = dailyQuery.data ? aggregateDailyCostTotals(dailyQuery.data.items) : [];
  const rankedRows = statsQuery.data ? rankByCost(statsQuery.data.items) : [];
  const maxRowCost = Math.max(1, ...rankedRows.map((row) => row.totalCostUsd));

  return (
    <div>
      <PageHeader title={resources.pages.cost.title} subtitle={resources.pages.cost.subtitle} />

      {isError && (
        <Card>
          <CardContent className="p-8 text-sm text-destructive">
            <p>{resources.pages.cost.loadError}</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{errorDetail}</p>
          </CardContent>
        </Card>
      )}

      {!isError && isLoading && (
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">{resources.common.loading}</CardContent>
        </Card>
      )}

      {!isError && !isLoading && (
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-[12.5px]">{resources.pages.cost.trendTitle}</CardTitle>
              <p className="text-[11px] text-muted-foreground">{resources.pages.cost.trendSubtitle}</p>
            </CardHeader>
            <CardContent className="pt-2">
              <CostTrendChart data={dailyTotals} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3.5">
              <CardTitle className="text-[12.5px]">{resources.pages.cost.rankingTitle}</CardTitle>
            </CardHeader>
            <CardContent>
              {rankedRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">{resources.pages.cost.emptyRanking}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {rankedRows.map((row) => {
                    const widthPct = row.totalCostUsd > 0 ? (row.totalCostUsd / maxRowCost) * 100 : 0;
                    return (
                      <div key={row.scope} className="flex items-center gap-3">
                        <div className="w-[170px] shrink-0 truncate text-xs text-muted-foreground">
                          {scopeLabel(row.scope)}
                        </div>
                        <div className="h-[18px] flex-1 overflow-hidden rounded bg-muted">
                          <div className="h-full rounded bg-primary" style={{ width: `${widthPct}%` }} />
                        </div>
                        <div className="w-16 shrink-0 text-right text-xs font-semibold tabular-nums">
                          {row.totalCostUsd > 0 ? formatCostUsd(row.totalCostUsd) : '—'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
